import { AlertTriangle, FileSpreadsheet, Plus } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { toast } from "sonner";

import { objectJosa } from "@buildpy/core/lib/josa";
import Banner from "@buildpy/ui/banner";
import EmptyState from "@buildpy/ui/empty-state";
import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { Input } from "@buildpy/ui/input";
import { Switch } from "@buildpy/ui/switch";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { formatDateTime, formatWon } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import {
  QuickPriceSuggestion,
  QuoteAmounts,
  RestoredDraft,
} from "@buildpy/core/modules/quote/action-types";
import {
  autosaveQuoteDraft,
  discardQuoteDraft,
  getPriceSuggestion,
  saveQuoteDraft,
  searchPriceSuggestions,
} from "@/api/actions/quote";
import { CalcQuoteResult, defaultRounding } from "@buildpy/core/modules/quote/calc";
import { CopyHead, LockReason, lockedNoteOf, strongerLockOf } from "@buildpy/core/modules/quote/lock-reason";
import { MissingSuggestion, suggestMissingItems } from "@buildpy/core/modules/quote/missing-rules";
import { safeCalcQuote } from "@buildpy/core/modules/quote/safe-calc";
import { QuoteDetail } from "@buildpy/core/modules/quote/types";
import Money from "@/components/money";
import AreaInput from "@/modules/quote/ui/editor/area-input";
import CoverEditor from "@/modules/quote/ui/editor/cover-editor";
import ImportDialog from "@/modules/quote/ui/editor/import-dialog";
import {
  contentKeyOf,
  historyKeyOf,
  historyReducer,
  initHistory,
} from "@buildpy/core/modules/quote/editor/history";
import {
  addedKeyOf,
  ignoredKeyOf,
  loadIgnored,
  missingAddable,
  missingAddAction,
  missingUndoOf,
  saveIgnored,
} from "@buildpy/core/modules/quote/editor/missing-add";
import MissingPanel from "@/modules/quote/ui/editor/missing-panel";
import {
  ackPendingResult,
  autosaveLabelOf,
  phoneAutosaveLabelOf,
  autosavePlanOf,
  autosaveSkipOf,
  clearMyDraft,
  discardAfterFlight,
  draftScopeOf,
  dropDraftOnCleanOf,
  dropPendingDraft,
  flightSkipOf,
  laterInstantOf,
  loadMyDraft,
  RestoreSource,
  restoreDecisionOf,
  revertExpectOf,
  revertOutcomeOf,
  sameInstant,
  saveMyDraft,
  savePendingDraft,
  takePendingDraft,
} from "@buildpy/core/modules/quote/editor/restore-offer";
import {
  amountsOfCalc,
  calcInputOf,
  canFillTemplate,
  fillTemplateNoteOf,
  initEditorState,
  mgmtPctError,
  mgmtPctSaveError,
  restoredStateOf,
  stateAreaM2,
  toDraftInput,
  vatModeOf,
  vatSwitchPriceCount,
} from "@buildpy/core/modules/quote/editor/reducer";
import SchedulePanel, {
  PhaseOption,
  ScheduleStandard,
  TradePhaseRef,
} from "@/modules/quote/ui/editor/schedule-panel";
import type { ProcessCalibration } from "@buildpy/core/modules/process-standard/calibration";
import TotalsPanel from "@/modules/quote/ui/editor/totals-panel";
import { lockedCopySession } from "@buildpy/core/modules/quote/editor/copy-edits";
import { usesArea } from "@buildpy/core/modules/quote/qty-expr";
import { leaveNeedsConfirm, LOCKED_LEAVE_CONFIRM } from "@buildpy/core/modules/quote/editor/leave-guard";
import {
  autosaveStaleOf,
  conflictNoteOf,
  elsewhereRevOf,
  localOnlyNoteOf,
  openSavedChannel,
  pendingSignalRevOf,
  postSaved,
  SAVED_ELSEWHERE_NOTE,
  SavedChannel,
  savedSignalActionOf,
  signalHeadOf,
  StaleSave,
  staleMarkOf,
  staleNoteOf,
  staleSaveAfterSyncOf,
} from "@buildpy/core/modules/quote/editor/saved-elsewhere";
import LockedBanner, { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";
import { clipboardCopyDeps } from "@/modules/quote/ui/editor/clipboard-copy";
import { openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import MobileEditor, { MobileEditorHead } from "@/modules/quote/ui/editor/mobile/mobile-editor";
import { usePhone } from "@/modules/quote/ui/editor/mobile/use-phone";
import QuickAdd from "@/modules/quote/ui/editor/quick-add";
import SaveTemplateDialog from "@/modules/quote/ui/editor/save-template-dialog";
import SheetView, { SHEET_START, SheetMemory } from "@/modules/quote/ui/editor/sheet/sheet-view";
import { SHEET_COMMIT_TOAST, slotToast } from "@/modules/quote/ui/editor/toast-slot";
import TradeCard from "@/modules/quote/ui/editor/trade-card";
import VatModeField from "@/modules/quote/ui/editor/vat-mode-field";
import {
  DEFAULT_SHOW_COST,
  EditorView,
  showCostCookieOf,
  TableMode,
  tableModeCookieOf,
  viewCookieOf,
} from "@buildpy/core/modules/quote/editor/view-pref";
import { FilledItemInput, ImportTradeInput, ItemRef } from "@buildpy/core/modules/quote/editor/types";
import {
  TRADE_GROUP_LABEL,
  TRADE_GROUPS,
  TradeGroup,
} from "@buildpy/core/modules/template/trades";
import { TemplateTrade } from "@buildpy/core/modules/template/types";

/**
 * 털어 낸 공종을 한 줄로 알린다.
 *
 * 이름을 전부 나열하지 않는다 — 새 견적은 템플릿의 15 공종을 안고 시작하므로,
 * 한 공종만 채우고 저장하면 14개가 줄줄이 찍혀 정작 저장 금액이 안 읽힌다.
 */
const DROPPED_NAME_LIMIT = 3;

// 빠른 추가 검색은 치기를 멈춘 뒤 이만큼 기다려 한 번만 묻는다(글자마다 물으면 멤버 분당 한도를 넘는다).
const QUICK_SEARCH_DELAY = 500;

// 부가세 방식 잠김으로 저장이 막혔을 때 토스트 설명 — 무엇을 하면 저장되는지.
const VAT_REVERT_NOTE = "부가세 포함·별도를 원래대로 돌린 뒤 다시 저장해 주세요";

const droppedMessage = (names: string[]): string | undefined => {
  if (names.length === 0) return undefined;
  if (names.length <= DROPPED_NAME_LIMIT) {
    return `값이 없어 빼 두었습니다 — ${names.join(" · ")}`;
  }
  return `값이 없는 공종 ${names.length}개는 빼 두었습니다 — ${names
    .slice(0, DROPPED_NAME_LIMIT)
    .join(" · ")} 외 ${names.length - DROPPED_NAME_LIMIT}개`;
};

/** 계산이 막힌 첫 렌더에서 보여 줄 자리. 실제로는 저장값이 먼저 쓰인다. */
const ZERO_AMOUNTS: QuoteAmounts = {
  subtotal_basic: 0,
  subtotal_style: 0,
  mgmt_fee: 0,
  gross: 0,
  rounding_adjustment: 0,
  total: 0,
  supply_amount: 0,
  vat_amount: 0,
};

// 글자를 치는 칸 — 여기서 ⌘Z 는 브라우저의 글자 되돌리기다(체크박스·버튼·select 는 아니다).
const NON_TEXT_INPUTS = new Set(["button", "checkbox", "radio", "submit", "reset", "file", "color", "range", "image"]);
const isTextEditable = (el: HTMLElement): boolean =>
  el.isContentEditable ||
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && !NON_TEXT_INPUTS.has(el.type));

// 시트 보기 기본값 경계 — md(641) 가 아니라 768: 641~767 은 레일(208px)이 떠서 내용 폭이 ~400px 라 시트가 비좁다.
const SHEET_VIEW_MIN_WIDTH = "768px";

// 이 탭에서 한 번 연 편집기 — 다시 열리면 라우터 캐시 값일 수 있어 새로 받는다.
const seenEditors = new Set<string>();

interface Props {
  detail: QuoteDetail;
  customerName: string;
  customerSite: string;
  /** 발송일로부터 며칠. 조직 설정값이고 여기서는 읽기만 한다. */
  validDays: number;
  templateTrades: TemplateTrade[];
  /** 편집기를 연 시점의 `quote.rev`. 충돌 판정의 기준이다. */
  baseRev: number;
  /** 쿠키로 기억한 시트·카드 보기. 없으면 null(화면 너비로 정한다). */
  initialView?: EditorView | null;
  /** 쿠키로 기억한 간단·상세 표 모드. 없으면 null(간단). */
  initialTableMode?: TableMode | null;
  // 쿠키로 기억한 원가·마진 보기 · 없으면 null(기본 보기).
  initialShowCost?: boolean | null;
  /** 지난번에 저장 못 하고 닫은 편집. 없으면 null. */
  restored: RestoredDraft | null;
  /** 편집하는 사람 — 이 탭에 적어 두는 초안 자리(sessionStorage)를 사용자별로 가른다. */
  memberId: string;
  /**
   * 고를 수 있는 공종. 「빌드파이 표준 + 이 회사」 를 서버가 합쳐 준다 —
   * 상수 15개를 그대로 쓰면 회사가 추가한 공종을 고를 수가 없다.
   */
  tradeOptions: EditorTradeOption[];
  /**
   * 공간 선택지(`codes.space`). 항목마다 「어디를 하느냐」 를 적어 두면
   * 상담에서 「욕실만 얼마」 를 그 자리에서 답할 수 있다.
   */
  spaces: { code: string; label: string }[];
  /**
   * 시공일정용. 공정 목록과 「어느 공종이 어느 공정인가」 를 서버가 합쳐 준다 —
   * 공종 표에만 있는 연결이라 편집기가 스스로 알 수 없다.
   */
  phases: PhaseOption[];
  tradePhases: TradePhaseRef[];
  // 시공일정 면적 초안(#170) — 견적에 고정된 공정 표준 판(없으면 최신)·회사 보정.
  standard?: ScheduleStandard | null;
  calibration?: ProcessCalibration;
  /** 「템플릿으로 저장」 을 보일지 — 대표만(서버 액션도 따로 막는다). */
  canSaveTemplate?: boolean;
}

export interface EditorTradeOption {
  code: string;
  name: string;
  group: TradeGroup;
  /** 견적서 목차에서의 자리. 공종 표가 정한다. */
  sort_order: number;
}

/**
 * 견적 편집기. 상태는 useReducer 하나뿐이고, 금액은 서버와 같은 calcQuote 로
 * 화면에서 즉시 계산한다 — 저장하면 서버가 계산한 값으로 바꿔 단다.
 */
const QuoteEditor = ({
  detail,
  customerName,
  customerSite,
  validDays,
  templateTrades,
  baseRev,
  initialView = null,
  initialTableMode = null,
  initialShowCost = null,
  restored,
  memberId,
  tradeOptions,
  spaces,
  phases,
  tradePhases,
  standard = null,
  calibration,
  canSaveTemplate = false,
}: Props) => {
  const router = useRouter();
  const [history, dispatch] = useReducer(historyReducer, detail, (d: QuoteDetail) =>
    initHistory(initEditorState(d))
  );
  const state = history.present;
  // 토스트 버튼처럼 나중에 불리는 콜백이 그 시점의 기록을 보게 한다.
  const historyRef = useRef(history);
  historyRef.current = history;
  const [saving, setSaving] = useState(false);
  // 저장 중복 막기 — 상단 단추·토스트 「다시 시도」·덮어쓰기가 같은 가드를 쓴다(state 는 한 박자 늦다).
  const savingRef = useRef(false);

  /* ── 자동저장 ──────────────────────────────────────────────── */

  /**
   * 충돌 판정의 기준. 「저장」 이 성공하면 서버가 준 새 번호로 갈아 끼운다 —
   * 옛 번호를 들고 있으면 자기가 방금 한 저장을 남의 것으로 읽는다.
   */
  const revRef = useRef(baseRev);
  const [autosavedAt, setAutosavedAt] = useState<string | null>(
    restored?.saved_at ?? null
  );
  const [stale, setStale] = useState(false);
  // 저장이 stale 로 막힌 번호·서버 문구 — 띠 문구가 되고, 같은 번호의 늦은 자동저장 응답이 stale 을 풀지 못하게 한다.
  const [staleSave, setStaleSaveState] = useState<StaleSave | null>(null);
  const staleSaveRef = useRef<StaleSave | null>(null);
  const setStaleSave = useCallback((next: StaleSave | null) => {
    staleSaveRef.current = next;
    setStaleSaveState(next);
  }, []);
  // 같은 브라우저의 다른 탭이 저장한 번호(null = 없음) — 이 탭 번호가 거기 닿을 때까지 새로 불러오라고 알린다.
  const [elsewhereRev, setElsewhereRev] = useState<number | null>(null);
  const savedElsewhere = elsewhereRev !== null;
  // 이 탭의 기준 번호를 바꾸는 한 곳 — 신호 번호 이상이 되면(최신본을 받았으면) 배너를 끈다.
  const syncRev = useCallback((rev: number) => {
    revRef.current = rev;
    setElsewhereRev((signal) => pendingSignalRevOf(signal, rev));
    setStaleSave(staleSaveAfterSyncOf(staleSaveRef.current, rev));
  }, [setStaleSave]);
  const savedChannelRef = useRef<SavedChannel | null>(null);
  const [others, setOthers] = useState<string[]>([]);
  /** 되살릴 편집이 있는지. 「이어쓰기」·「버리기」 를 누르면 사라진다. */
  const [offer, setOfferState] = useState<{ draft: RestoredDraft; source: RestoreSource } | null>(null);
  // 효과·자동저장 콜백이 지금 배너를 보게 한다(배너가 떠 있는 동안은 판정도 pending 기록도 멈춘다).
  const offerRef = useRef(offer);
  const setOffer = useCallback((next: { draft: RestoredDraft; source: RestoreSource } | null) => {
    offerRef.current = next;
    setOfferState(next);
  }, []);
  const [discarding, setDiscarding] = useState(false);
  // 이 탭이 마지막으로 자동저장에 성공한 시각 — 편집기가 다시 서면 sessionStorage(loadMyDraft)에서 이어 받는다.
  const ownSavedAtRef = useRef<string | null>(null);
  const restoredRef = useRef(restored);
  restoredRef.current = restored;
  /**
   * 자동저장 세대. 「저장」·「버리기」 가 올린다.
   *
   * 올리지 않으면 이런 일이 난다 — 타이머가 자동저장을 보낸 직후 사람이
   * 「저장」 을 누르면, 늦게 도착한 자동저장이 방금 지운 초안을 되살리고
   * 옛 번호로 `stale` 을 true 로 만들어 **자기 저장 직후에** 경고가 뜬다.
   */
  const autosaveGen = useRef(0);
  const [autosaveFailed, setAutosaveFailedState] = useState(false);
  // 떠날 때 렌더를 기다리지 않고 읽는 「마지막 자동저장 실패」 — 켜져 있고 새 편집이 없으면 받지 못한 편집을 pending 으로 적는다.
  const autosaveFailedRef = useRef(false);
  const setAutosaveFailed = useCallback((next: boolean) => {
    autosaveFailedRef.current = next;
    setAutosaveFailedState(next);
  }, []);
  // 자동저장이 새 버전(다른 탭·사람의 저장)에 밀려 적히지 않는 중 — 「자동저장됨」 대신 새로고침을 권한다.
  const [superseded, setSupersededState] = useState(false);
  // 떠날 때(pagehide) 렌더를 기다리지 않고 읽는 superseded — 켜져 있으면 저장 안 된 편집을 pending 으로 적는다.
  const supersededRef = useRef(false);
  const setSuperseded = useCallback((next: boolean) => {
    supersededRef.current = next;
    setSupersededState(next);
  }, []);
  // 그 사이 발송 등으로 더 못 고치는 견적이 됐다 — 그 까닭(lockedNoteOf 로 문구)을 들고 있다.
  const [lockReason, setLockReason] = useState<LockReason | null>(null);
  const locked = lockReason !== null;
  const lockNote = lockReason === null ? undefined : lockedNoteOf(lockReason);
  // 잠김을 토스트로 이미 읽혔다 — live 영역이 같은 말을 두 번 읽지 않게.
  const [lockToasted, setLockToasted] = useState(false);
  // 비동기 콜백(자동저장·저장)이 렌더를 기다리지 않고 바로 읽는 잠김 까닭.
  const lockedRef = useRef<LockReason | null>(null);
  // 잠긴 뒤 「편집 내용 복사」 의 비교 기준 — 첫 화면 상태에서 잡는다(lockedCopySession).
  const [copySession] = useState(() => lockedCopySession(state));
  const markLocked = useCallback((reason: LockReason, serverHead?: CopyHead, toasted = false) => {
    lockedRef.current = reason;
    copySession.locked(serverHead);
    setLockToasted(toasted);
    setLockReason(reason);
  }, [copySession]);
  // 늦게 도착하는 dispatch(단가 제안·토스트 되돌리기·빠른 추가) — 들어오는 순간 잠겨 있으면 버린다.
  const dispatchUnlessLocked = useMemo(() => unlessLocked(() => lockedRef.current !== null, dispatch), []);
  // 자식이 await·setTimeout 뒤에 렌더를 기다리지 않고 읽는 잠김.
  const isLocked = useCallback(() => lockedRef.current !== null, []);
  // 비동기로 닿는 잠김(자동저장 응답·발송 신호) — 이미 잠겼으면 다시 잠그지 않는다(live 안내·복사 기준을 다시 쓰지 않게).
  const markLockedOnce = useMemo(() => unlessLocked(isLocked, markLocked), [isLocked, markLocked]);
  // 다른 탭의 잠김 신호 — 처음이면 잠그고, 이미 잠겼으면 더 뒤 단계 까닭으로만 문구를 바꾼다(복사 기준은 그대로).
  const lockFromSignal = useCallback((reason: LockReason, serverHead?: CopyHead) => {
    const current = lockedRef.current;
    if (current === null) return markLocked(reason, serverHead);
    const next = strongerLockOf(current, reason);
    if (next === current) return;
    lockedRef.current = next;
    setLockToasted(false);
    setLockReason(next);
  }, [markLocked]);
  // 서버 초안을 버리는 중 — 그 사이 자동저장은 미뤘다가 끝난 뒤 보낸다(버리기가 방금 적은 편집을 지우지 않게).
  const discardingRef = useRef(false);
  // 이어쓰기(RESTORE)마다 올린다 — 그 전에 띄운 토스트의 「되돌리기」 가 갈린 편집을 키로 오판하지 않게.
  const restoreEpochRef = useRef(0);
  // 시트가 무효 입력을 버린 시각 — 곧이어 저장하면 저장 토스트가 취소 토스트 자리를 대신한다.
  const abandonedRef = useRef<{ text: string; at: number } | null>(null);
  const onAbandon = useCallback((text: string) => {
    abandonedRef.current = { text, at: Date.now() };
  }, []);
  const importButtonRef = useRef<HTMLButtonElement | null>(null);

  /**
   * 간단 모드. 자재·노무를 나눠 쓰는 회사는 소수라 **기본은 간단**이다.
   *
   * 편집기 상태가 아니라 화면 설정이라 여기 둔다 — 초안에 섞이면 이어쓰기로
   * 되살릴 때 남의 화면 설정까지 따라온다.
   */
  const [simple, setSimpleState] = useState(initialTableMode !== "detail");
  const setSimple = (next: boolean) => {
    setSimpleState(next);
    document.cookie = tableModeCookieOf(next ? "simple" : "detail");
  };

  // 원가·마진 보기 — 화면 설정이라 초안이 아니라 쿠키에 둔다.
  const [showCost, setShowCostState] = useState(initialShowCost ?? DEFAULT_SHOW_COST);
  const setShowCost = (next: boolean) => {
    setShowCostState(next);
    document.cookie = showCostCookieOf(next);
  };

  // 시트·카드 보기 — 쿠키로 기억한 값(서버가 넘김)이 없으면 화면 너비(768px)로 한 번 정해 적어 둔다.
  const [view, setViewState] = useState<EditorView>(initialView ?? "card");
  const sheetMemory = useRef<SheetMemory | null>(null);
  const setView = (next: EditorView) => {
    setViewState(next);
    document.cookie = viewCookieOf(next);
  };
  useEffect(() => {
    if (initialView) return;
    const next: EditorView = window.matchMedia(`(min-width: ${SHEET_VIEW_MIN_WIDTH})`).matches ? "sheet" : "card";
    setViewState(next);
    document.cookie = viewCookieOf(next);
  }, [initialView]);

  /* ── 빠른 추가 ────────────────────────────────────────────── */

  const [quickHits, setQuickHits] = useState<QuickPriceSuggestion[]>([]);
  /**
   * 마지막으로 보낸 검색의 번호. 늦게 온 답이 새 글자의 답을 덮지 않게 한다 —
   * 「실크」 를 치는 동안 「실」 의 답이 나중에 닿으면 목록이 뒤로 간다.
   */
  const quickGen = useRef(0);
  // 아직 보내지 않은 검색 — 새 글자·담기·화면 닫기에 지운다.
  const quickTimer = useRef<number | undefined>(undefined);
  const cancelQuickSearch = useCallback(() => window.clearTimeout(quickTimer.current), []);
  useEffect(() => cancelQuickSearch, [cancelQuickSearch]);

  const quickSearch = useCallback(
    (tradeKey: string, keyword: string) => {
      cancelQuickSearch();
      const trade = stateRef.current.trades.find((t) => t.key === tradeKey);
      if (!trade || keyword.trim() === "") {
        setQuickHits([]);
        return;
      }
      quickTimer.current = window.setTimeout(() => {
        quickGen.current += 1;
        const gen = quickGen.current;
        void searchPriceSuggestions(trade.trade, keyword, vatModeOf(stateRef.current))
          .then((rows) => {
            if (gen !== quickGen.current) return;
            setQuickHits(rows);
          })
          .catch(() => setQuickHits([]));
      }, QUICK_SEARCH_DELAY);
    },
    [cancelQuickSearch]
  );

  const quickAdd = useCallback((tradeKey: string, item: FilledItemInput) => {
    cancelQuickSearch();
    dispatchUnlessLocked({ type: "ADD_FILLED_ITEM", tradeKey, item });
    setQuickHits([]);
  }, [cancelQuickSearch, dispatchUnlessLocked]);

  const quickPaste = useCallback(
    (tradeKey: string, items: FilledItemInput[]) => {
      cancelQuickSearch();
      dispatchUnlessLocked({ type: "ADD_FILLED_ITEMS", tradeKey, items });
      setQuickHits([]);
    },
    [cancelQuickSearch, dispatchUnlessLocked]
  );

  const [importOpen, setImportOpen] = useState(false);
  const openImport = useCallback(() => setImportOpen(true), []);
  // 폰 머리 더보기에서 여는 「빠른 추가」 시트 · 「템플릿으로 저장」 창.
  const [quickOpen, setQuickOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  // 닫히면 연 단추로 — 시트·카드 모두 「엑셀 가져오기」 단추 하나(ref)라 복귀는 onCloseAutoFocus 한 곳뿐이다.
  const returnImportFocus = useCallback(() => {
    importButtonRef.current?.focus({ preventScroll: true });
  }, []);
  // 폰 머리 「더보기」 — 메뉴에서 연 창·시트는 Trigger 없이 상태로 열려 Radix 가 body 로 돌려보내니 여기로 직접 돌린다.
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnMoreFocus = useCallback(() => {
    moreButtonRef.current?.focus({ preventScroll: true });
  }, []);
  // 잠긴 뒤 들어온 가져오기 확인은 버린다(창은 잠기면 닫힌다).
  const onImport = useMemo(
    () =>
      unlessLocked(
        () => lockedRef.current !== null,
        (trades: ImportTradeInput[], rowCount: number) => {
          dispatch({ type: "IMPORT_TRADES", trades });
          toast.success(`${rowCount}줄을 추가했습니다`, {
            description: "되돌리기(⌘Z)로 한 번에 취소할 수 있습니다",
          });
        }
      ),
    []
  );

  // 비동기 콜백이 최신 상태를 보게 한다. 콜백을 상태에 묶으면 memo 가 매번 깨진다.
  const stateRef = useRef(state);
  stateRef.current = state;

  const quote = detail.quote;
  const draftScope = draftScopeOf(memberId, quote.id);
  const detailHref = `/app/quotes/${quote.id}`;

  /**
   * 금액은 렌더 중에 계산한다. `calcQuote` 는 절사액이 총계를 넘으면 던지는데,
   * 그 예외가 렌더에서 나면 편집기가 통째로 언마운트되어 미저장 편집이 사라진다.
   * 그래서 래퍼로 받아 실패하면 **마지막 정상값을 그대로 두고 저장만 막는다**.
   */
  const calc = useMemo(() => safeCalcQuote(calcInputOf(state)), [state]);
  const lastGood = useRef<CalcQuoteResult | null>(null);
  const liveResult = useMemo(() => {
    if (calc.ok) lastGood.current = calc.result;
    return calc.ok ? calc.result : lastGood.current;
  }, [calc]);

  const liveAmounts: QuoteAmounts = useMemo(
    () => (liveResult ? amountsOfCalc(liveResult) : state.saved ?? ZERO_AMOUNTS),
    [liveResult, state.saved]
  );
  const vatMode = vatModeOf(state);

  const calcError = calc.ok ? null : calc.message;
  /**
   * 화면에 빨간 줄을 띄우는 기준. **치는 동안의 빈 칸은 봐준다** — 고치려고 지운
   * 0.2초 동안 경고가 깜빡이면 거슬린다.
   */
  const pctError = mgmtPctError(state.mgmt_fee_pct);
  /**
   * **저장을 누르는 순간의 기준은 한 칸 엄격하다.** 빈 칸을 그대로 저장하면
   * `mgmtRateOf("")` 가 0 을 돌려주어 관리비가 0% 로 굳는다 — 5,000만원 견적에서
   * 4% 면 200만원이 소리 없이 사라지고 그대로 고객에게 나간다.
   *
   * 화면을 막지 않고 **저장할 때만** 말하는 이유: 지우고 다시 치는 것이 정상적인
   * 손놀림이라, 그 사이에 화면을 잠그면 고치는 일이 더 번거로워진다.
   */
  const pctSaveError = mgmtPctSaveError(state.mgmt_fee_pct);
  const blocked = calcError !== null || pctError !== null;

  const shown = state.dirty || !state.saved ? liveAmounts : state.saved;

  const templateByTrade = useMemo(
    () => new Map(templateTrades.map((t) => [t.trade, t.items])),
    [templateTrades]
  );

  const needsArea = useMemo(
    () =>
      state.area_pyeong === null &&
      state.trades.some((t) =>
        t.items.some(
          (i) => usesArea(i.qty_expr ?? "") || i.children.some((c) => usesArea(c.qty_expr ?? ""))
        )
      ),
    [state.area_pyeong, state.trades]
  );

  /* ── 빠진 항목 제안(편집기 전용, 무시 목록은 이 브라우저에만) ── */

  const [ignored, setIgnored] = useState<string[]>([]);
  // 견적 계열(버전끼리 같은 견적번호) 단위 — 새 버전에서도 무시가 이어진다. 예전 id 키는 비었을 때만 읽는다.
  const ignoredKey = ignoredKeyOf(quote);
  useEffect(() => {
    setIgnored(loadIgnored(ignoredKey, quote.id));
  }, [ignoredKey, quote.id]);
  const updateIgnored = useCallback(
    (next: string[]) => {
      setIgnored(next);
      saveIgnored(ignoredKey, next);
    },
    [ignoredKey]
  );
  const allMissing = useMemo(
    () => suggestMissingItems({ trades: state.trades, area_pyeong: state.area_pyeong }),
    [state.trades, state.area_pyeong]
  );
  const missing = useMemo(
    () => allMissing.filter((s) => !ignored.includes(s.id)),
    [allMissing, ignored]
  );
  // 추가한 줄로 시트 커서를 옮기려고 공종 코드를 들고 있다가, 상태가 바뀐 뒤 그 공종의 마지막 줄을 찾는다.
  const pendingJumpRef = useRef<string | null>(null);
  const [sheetJump, setSheetJump] = useState<{ itemKey: string; seq: number } | null>(null);
  useEffect(() => {
    const code = pendingJumpRef.current;
    if (code === null) return;
    const last = state.trades.find((t) => t.trade === code)?.items.at(-1);
    if (!last) return;
    pendingJumpRef.current = null;
    setSheetJump((prev) => ({ itemKey: last.key, seq: (prev?.seq ?? 0) + 1 }));
  }, [state.trades]);
  const canAddMissing = useCallback(
    (s: MissingSuggestion) => missingAddable(state, s, tradeOptions),
    [state, tradeOptions]
  );
  const onJumped = useCallback(() => setSheetJump(null), []);
  const addMissing = useCallback(
    (s: MissingSuggestion) => {
      if (lockedRef.current !== null) return;
      const action = missingAddAction(stateRef.current, s, tradeOptions);
      if (!action) return;
      // 커서 옮기기는 시트에서만 — 카드에서 들고 있으면 나중에 시트로 바꿀 때 옛 줄로 튄다.
      if (view === "sheet") pendingJumpRef.current = s.trade_code;
      const from = historyRef.current.present;
      const addedKey = addedKeyOf(action);
      const epoch = restoreEpochRef.current;
      dispatch(action);
      toast.success(`「${s.suggestion.name}」${objectJosa(s.suggestion.name)} 추가했습니다`, {
        description:
          view === "sheet"
            ? "단가 칸으로 옮겼습니다 — 값을 넣어 주세요"
            : `${action.trades[0]?.name ?? ""} 공종 끝에 넣었습니다 — 단가를 넣어 주세요`,
        action: {
          label: "되돌리기",
          onClick: () => {
            const undo = missingUndoOf(historyRef.current, from, addedKey, restoreEpochRef.current !== epoch);
            if (undo === "undo") {
              dispatchUnlessLocked({ type: "UNDO_STEP", from });
            } else if (undo === "gone") {
              toast.info("추가한 줄은 이미 되돌렸습니다");
            } else if (undo === "replaced") {
              toast.info("되살린 편집으로 바뀌어 되돌리지 않았습니다");
            } else {
              toast.info("그 뒤에 고친 내용이 있어 되돌리지 않았습니다", {
                description: "⌘Z(Ctrl+Z)로 한 단계씩 되돌려 주세요",
              });
            }
          },
        },
      });
    },
    [tradeOptions, view, dispatchUnlessLocked]
  );

  // 「공종 추가」 메뉴 — 잠기면 열려 있어도 닫는다(포털이라 fieldset 이 못 끈다).
  const [tradeMenuOpen, setTradeMenuOpen] = useState(false);
  const missingTrades = useMemo(
    () =>
      tradeOptions.filter(
        (def) => !state.trades.some((t) => t.trade === def.code)
      ),
    [state.trades, tradeOptions]
  );

  // 카드 보기의 ⌘Z·⌘⇧Z — 입력칸 안은 브라우저 기본(글자 되돌리기)에 맡기고, 그 밖에서만 편집을 되돌린다.
  const editorRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (view !== "card") return;
    const onKeyDown = (e: KeyboardEvent) => {
      const command = historyKeyOf(e);
      if (command === null || e.defaultPrevented || lockedRef.current !== null) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target && isTextEditable(target)) return;
      const inEditor = !target || target === document.body || editorRootRef.current?.contains(target);
      if (!inEditor) return;
      e.preventDefault();
      dispatch({ type: command });
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [view]);

  // 폰(md 미만)은 같은 상태·dispatch 로 폰 전용 뷰를 그린다 — null(서버·첫 그림)이면 둘 다 그리고 CSS 가 가른다.
  const phone = usePhone();

  /* ── 이탈 경고 ─────────────────────────────────────────────── */

  // 새로고침·창 닫기는 고친 게 있으면 늘 묻고, 앱 안 링크는 잠긴 뒤에만 묻는다. 뒤로가기는 배너 문구에 맡긴다.
  useLeaveGuard({
    unload: state.dirty,
    links: leaveNeedsConfirm({ dirty: state.dirty, locked }),
    message: LOCKED_LEAVE_CONFIRM,
  });

  /* ── 단가 자동완성 ─────────────────────────────────────────── */

  const onSuggest = useCallback((ref: ItemRef) => {
    const current = stateRef.current;
    const trade = current.trades.find((t) => t.key === ref.tradeKey);
    if (!trade) return;

    const parent = ref.parentKey
      ? trade.items.find((i) => i.key === ref.parentKey)
      : null;
    const list = parent ? parent.children : trade.items;
    const item = list.find((i) => i.key === ref.itemKey);
    if (!item) return;

    // SET 부모는 단가를 쓰지 않고, 이미 넣은 단가는 덮지 않는다.
    if (item.children.length > 0) return;
    if (item.item.trim().length === 0) return;
    if (item.material_unit_price > 0 || item.labor_unit_price > 0) return;

    void (async () => {
      const price = await getPriceSuggestion(
        trade.trade,
        item.scope.trim(),
        item.item.trim(),
        item.unit.trim(),
        vatModeOf(current)
      );
      if (!price) return;
      if (price.material_unit_price === 0 && price.labor_unit_price === 0) {
        return;
      }
      dispatchUnlessLocked({
        type: "SUGGEST_PRICE",
        ref,
        material_unit_price: price.material_unit_price,
        labor_unit_price: price.labor_unit_price,
        cost_unit_price: price.cost_unit_price,
        base_margin_bp: price.base_margin_bp,
      });
    })().catch(() => {
      // 단가 제안은 있으면 좋은 것이다. 못 가져왔다고 토스트를 띄우면 칸을 옮길
      // 때마다 뜬다 — 조용히 넘긴다(바로 위 「빠른 추가」 제안과 같은 규칙).
    });
  }, [dispatchUnlessLocked]);

  /* ── 저장 ──────────────────────────────────────────────────── */

  /**
   * 자동저장. 멈칫할 때마다(1.5초) 보낸다.
   *
   * 본문 표를 건드리지 않으므로 **성립하지 않는 값이어도 보낸다** — 수량을 지우는
   * 중이라고 이어쓰기 거리가 끊기면 안 된다. 검증은 「저장」 이 한다.
   */
  const autosavePending = useRef(false);
  // 보냈지만 응답이 아직인 자동저장 — 그 사이 떠나면 요청이 끊길 수 있어 내용째 pending 으로 적는다.
  const inflightRef = useRef<{ data: unknown; rev: number; token: string | null; done?: Promise<string | null> } | null>(null);
  // 떠나며 보낸(또는 응답 전인) 초안을 내용째 적어 둔다 — pending 배너가 떠 있으면 그 pending 을 덮지 않는다.
  const recordPending = useCallback(
    (data: unknown, rev: number): string | null => {
      if (offerRef.current?.source === "pending") return null;
      const token = `${Date.now()}-${Math.random()}`;
      savePendingDraft(draftScope, {
        token,
        prev: ownSavedAtRef.current ?? loadMyDraft(draftScope),
        base_rev: rev,
        at: new Date().toISOString(),
        data,
      });
      return token;
    },
    [draftScope]
  );
  const sendAutosave = useCallback((leaving = false) => {
    const block =
      lockedRef.current !== null
        ? "locked"
        : supersededRef.current
          ? "superseded"
          : autosaveFailedRef.current && !autosavePending.current
            ? "failed"
            : null;
    const plan = autosavePlanOf(block, leaving, stateRef.current.dirty);
    if (plan === "skip") return;
    if (plan === "record") {
      recordPending(stateRef.current, revRef.current);
      return;
    }
    if (discardingRef.current && !leaving) return;
    if (!autosavePending.current || !stateRef.current.dirty) {
      const flight = inflightRef.current;
      if (leaving && flight && flight.token === null) flight.token = recordPending(flight.data, flight.rev);
      return;
    }
    autosavePending.current = false;
    const gen = autosaveGen.current;
    const data = stateRef.current;
    const sentRev = revRef.current;
    const flight: { data: unknown; rev: number; token: string | null; done?: Promise<string | null> } = {
      data,
      rev: sentRev,
      token: leaving ? recordPending(data, sentRev) : null,
    };
    inflightRef.current = flight;
    const request = autosaveQuoteDraft(quote.id, data, sentRev);
    // 끝나기를 기다리는 쪽(버리기)에 이 회차가 적은 시각을 넘긴다 — 못 적었으면 null.
    flight.done = request.then(
      (r) => (r.ok ? r.saved_at : null),
      () => null
    );
    void request
      .then((result) => {
        if (inflightRef.current === flight) inflightRef.current = null;
        const token = flight.token;
        if (token) ackPendingResult(draftScope, token, result);
        // 새로고침이 이 응답보다 먼저 닿아 내 초안을 배너로 띄웠으면 거둔다.
        if (result.ok && offerRef.current && sameInstant(offerRef.current.draft.saved_at, result.saved_at)) {
          setOffer(null);
        }
        // 보낸 뒤에 「저장」·「버리기」 가 있었으면 이 응답은 낡은 것이다 — 그래도 잠김·밀림은 반영한다(flightSkipOf).
        const stale = gen !== autosaveGen.current;
        if (!result.ok) {
          const skip = flightSkipOf(autosaveSkipOf(result), stale, sentRev, revRef.current);
          if (skip === "locked") {
            markLockedOnce(("lock_reason" in result && result.lock_reason) || "sent", "lock_head" in result ? result.lock_head : undefined);
          }
          else if (skip === "superseded") {
            setSuperseded(true);
            setStale(true);
          } else if (skip === "failed") setAutosaveFailed(true);
          return;
        }
        if (stale) return;
        setSuperseded(false);
        // 동시 비행의 응답이 역순이면 옛 시각이 늦게 닿는다 — 늦은 시각만 남긴다.
        ownSavedAtRef.current = laterInstantOf(ownSavedAtRef.current, result.saved_at);
        if (!token && offerRef.current?.source !== "pending") saveMyDraft(draftScope, result.saved_at);
        setAutosaveFailed(false);
        setAutosavedAt(result.saved_at);
        // stale 이면 보낸 번호에 표식을 남긴다 — 앞서 보낸 회차의 늦은 stale=false 가 풀지 못하게.
        setStaleSave(staleMarkOf(result.stale, sentRev, staleSaveRef.current));
        setStale(autosaveStaleOf(result.stale, sentRev, staleSaveRef.current));
        setOthers(result.others);
      })
      .catch(() => {
        if (inflightRef.current === flight) inflightRef.current = null;
        // 토스트 대신 머리의 「저장 실패 — 다시 시도」 로 알린다 — 타이핑 중에 토스트가 계속 뜨지 않게.
        if (gen === autosaveGen.current) setAutosaveFailed(true);
      });
  }, [quote.id, draftScope, recordPending, setOffer, markLockedOnce, setSuperseded, setAutosaveFailed, setStaleSave]);

  const retryAutosave = useCallback(() => {
    autosavePending.current = true;
    sendAutosave();
  }, [sendAutosave]);

  // 앱 안 뒤로가기(라우터 캐시)·bfcache 복원은 옛 서버 값을 보여 준다 — 한 번 새로 받아 이어쓰기 배너를 맞춘다.
  const refreshChecked = useRef(false);
  useEffect(() => {
    // 엄격 모드의 두 번째 실행은 같은 인스턴스라 ref 로 한 번만 본다.
    if (!refreshChecked.current) {
      refreshChecked.current = true;
      if (seenEditors.has(quote.id)) router.refresh();
      seenEditors.add(quote.id);
    }
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) router.refresh();
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, [quote.id, router]);

  useEffect(() => {
    const channel = openSavedChannel();
    if (!channel) return;
    savedChannelRef.current = channel;
    channel.onmessage = (e) => {
      const action = savedSignalActionOf(e.data, quote.id, revRef.current);
      // 잠김 신호(발송·수주·이탈·새 버전)면 띠 대신 잠근다 — 발송 신호에 실린 머리를 복사 기준으로(없으면 첫 화면 머리).
      if (action === "saved") setElsewhereRev((signal) => elsewhereRevOf(signal, e.data, quote.id, revRef.current));
      else if (action !== "none") lockFromSignal(action, signalHeadOf(e.data));
    };
    return () => {
      channel.close();
      if (savedChannelRef.current === channel) savedChannelRef.current = null;
    };
  }, [quote.id, lockFromSignal]);

  useEffect(() => {
    autosavePending.current = state.dirty;
    if (!state.dirty) return;
    const timer = window.setTimeout(() => sendAutosave(), 1_500);
    return () => window.clearTimeout(timer);
  }, [state, sendAutosave]);

  // 앱 안에서 떠나거나(언마운트) 탭을 숨기거나 닫으면 기다리던 초안을 바로 보낸다.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") sendAutosave();
    };
    const onPageHide = () => sendAutosave(true);
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onPageHide);
      sendAutosave(true);
    };
  }, [sendAutosave]);

  const applyDraft = useCallback(
    (draft: RestoredDraft): boolean => {
      const next = restoredStateOf(draft.data, quote.rough_area_pyeong, quote.vat_mode, quote.area_m2 ?? null);
      if (!next) return false;
      restoreEpochRef.current += 1;
      dispatch({ type: "RESTORE", state: next });
      if (draft.base_rev !== baseRev) {
        // 되살린 내용은 **그때의 번호**를 기준으로 쓴 것이다. 기준을 현재 번호로
        // 두면 서버의 `isStale` 이 통과해 버려, 배너만 뜨고 「저장」 은 그대로
        // 남의 작업을 덮는다 — 화면 경고와 서버 판정이 어긋나면 안 된다.
        syncRev(draft.base_rev);
        setStale(true);
      }
      return true;
    },
    [baseRev, quote.rough_area_pyeong, quote.vat_mode, quote.area_m2, syncRev]
  );
  // 효과가 baseRev 변화(저장 성공)로 다시 돌면 지운 초안을 다시 물으므로 ref 로만 읽는다.
  const applyDraftRef = useRef(applyDraft);
  applyDraftRef.current = applyDraft;
  const baseRevRef = useRef(baseRev);
  baseRevRef.current = baseRev;

  // 이어쓰기 판정(restoreDecisionOf)을 화면에 옮긴다 — withPending=false 는 pending 을 버린 뒤 서버 초안만으로 다시 볼 때.
  const judgeRestore = useCallback(
    (withPending: boolean) => {
      const current = restoredRef.current;
      const readable = (data: unknown) => restoredStateOf(data, quote.rough_area_pyeong, quote.vat_mode, quote.area_m2 ?? null) !== null;
      const pending = withPending ? takePendingDraft(draftScope, readable) : null;
      const own = ownSavedAtRef.current ?? loadMyDraft(draftScope);
      const decision = restoreDecisionOf(current, own, baseRevRef.current, pending);
      const dirty = historyRef.current.present.dirty;
      if (decision.kind === "none") return;
      if (decision.kind === "offer") {
        // 이미 이어 쓰는 중인 내 초안이면 다시 묻지 않는다 — 그 뒤 저장한 사람은 stale 배너가 알린다.
        if (!(decision.mine && dirty)) setOffer({ draft: decision.draft, source: decision.source });
        return;
      }
      // 이미 고치기 시작했거나 잠긴 견적이면 덮지 않는다 — 편집은 곧 자동저장되고, 잠긴 견적엔 옛 편집을 되살리지 않는다.
      if (dirty || lockedRef.current !== null) return;
      if (applyDraftRef.current(decision.draft)) {
        if (decision.source === "server") {
          ownSavedAtRef.current = decision.draft.saved_at;
          saveMyDraft(draftScope, decision.draft.saved_at);
        }
        return;
      }
      if (decision.source === "pending") {
        dropPendingDraft(draftScope, ownSavedAtRef.current);
        judgeRestore(false);
        return;
      }
      setOffer({ draft: decision.draft, source: decision.source });
    },
    [draftScope, quote.rough_area_pyeong, quote.vat_mode, quote.area_m2, setOffer]
  );

  const restore = useCallback(() => {
    if (offer && applyDraft(offer.draft)) {
      setOffer(null);
      toast.success("저장 안 된 편집을 되살렸습니다");
      return;
    }
    toast.error("이어쓸 내용을 읽지 못했습니다");
    setOffer(null);
    if (offer?.source !== "pending") return;
    dropPendingDraft(draftScope, ownSavedAtRef.current);
    judgeRestore(false);
  }, [offer, applyDraft, draftScope, judgeRestore, setOffer]);
  // 잠긴 견적엔 옛 편집을 되살리지 않는다 — 단추도 꺼지지만 늦은 클릭까지 막는다.
  const restoreUnlessLocked = useMemo(() => unlessLocked(() => lockedRef.current !== null, restore), [restore]);

  // 서버가 준 이어쓰기 거리(뒤로가기면 캐시 값 → 새로고침 값 순으로 온다) — 배너가 떠 있으면 사람이 고를 때까지 그대로 둔다.
  const restoredAt = restored?.saved_at ?? null;
  const judgeRestoreRef = useRef(judgeRestore);
  judgeRestoreRef.current = judgeRestore;
  useEffect(() => {
    const current = restoredRef.current;
    if (current) setAutosavedAt(current.saved_at);
    if (offerRef.current) return;
    judgeRestoreRef.current(true);
  }, [restoredAt, quote.id]);

  const discard = useCallback(async () => {
    // pending 배너면 이 탭에 적어 둔 것만 버리고 서버 초안으로 다시 판정한다 — 서버의 정상 초안까지 지우지 않는다.
    if (offerRef.current?.source === "pending") {
      dropPendingDraft(draftScope, ownSavedAtRef.current);
      setOffer(null);
      judgeRestore(false);
      return;
    }
    if (discardingRef.current) return;
    autosaveGen.current += 1;
    const flight = inflightRef.current;
    inflightRef.current = null;
    discardingRef.current = true;
    setDiscarding(true);
    const result = await discardAfterFlight(flight?.done, () => discardQuoteDraft(quote.id));
    discardingRef.current = false;
    setDiscarding(false);
    // 못 지웠으면 배너를 남긴다 — 지운 줄 알고 떠나면 다음에 열 때 같은 초안을 또 묻는다.
    if (!result?.ok) {
      toast.error("편집을 버리지 못했습니다", {
        description: result?.message ?? "잠시 뒤 다시 시도해 주세요",
      });
    } else {
      setOffer(null);
      setAutosavedAt(null);
      setAutosaveFailed(false);
      // 밀림(superseded)은 초안이 아니라 견적 번호의 사실이라 버려도 그대로 둔다.
      ownSavedAtRef.current = null;
      clearMyDraft(draftScope);
    }
    // 배너가 떠 있는 동안 친 편집(화면에 남음)을 버리기 뒤에 다시 적는다 — 저장본과 같으면 · 그 사이 잠겼으면 보내지 않는다.
    if (lockedRef.current !== null) return;
    autosavePending.current = true;
    sendAutosave();
  }, [quote.id, draftScope, judgeRestore, setOffer, sendAutosave, setAutosaveFailed]);

  // 되돌려 저장본에 닿으면(dirty 가 내려가면) 이 탭이 적은 서버 초안을 지운다 — 남기면 다시 열 때 되돌리기 전 편집이 되살아난다.
  const wasDirtyRef = useRef(state.dirty);
  useEffect(() => {
    const wasDirty = wasDirtyRef.current;
    wasDirtyRef.current = state.dirty;
    const drop = dropDraftOnCleanOf({
      wasDirty,
      dirty: state.dirty,
      ownDraft: ownSavedAtRef.current !== null,
      inflight: inflightRef.current !== null,
      offer: offerRef.current !== null,
      block:
        lockedRef.current !== null
          ? "locked"
          : supersededRef.current
            ? "superseded"
            : autosaveFailedRef.current
              ? "failed"
              : null,
    });
    if (!drop || discardingRef.current) return;
    autosaveGen.current += 1;
    const flight = inflightRef.current;
    inflightRef.current = null;
    discardingRef.current = true;
    // 슬롯이 이 탭이 마지막으로 적은 초안일 때만 지운다 — 같은 사람 다른 탭이 그 뒤에 적은 초안은 남긴다.
    void discardAfterFlight(flight?.done, (flown) => {
      const expect = revertExpectOf(flown, ownSavedAtRef.current);
      // 이 탭이 적은 초안이 확인되지 않았으면(첫 자동저장이 못 적힘) 서버에 지울 내 것이 없다.
      return expect === null ? Promise.resolve({ ok: true as const }) : discardQuoteDraft(quote.id, expect);
    }).then((result) => {
      discardingRef.current = false;
      const outcome = revertOutcomeOf(result);
      if (outcome !== "failed") {
        setAutosavedAt(null);
        setAutosaveFailed(false);
        ownSavedAtRef.current = null;
        clearMyDraft(draftScope);
      }
      if (outcome === "kept") {
        toast.info("서버의 이어쓰기 초안은 지우지 않았습니다", {
          description: "서버 초안이 그 사이 바뀌어(다른 탭·기기의 편집 또는 저장) 지우지 않았습니다 — 다시 열면 이어쓸지 묻습니다",
        });
      } else if (outcome === "failed") {
        toast.error("되돌린 상태를 서버에 적지 못했습니다", {
          description: "다시 열면 되돌리기 전 편집이 되살아날 수 있습니다 — 저장하거나 잠시 뒤 다시 시도하세요",
        });
      }
      // 지우는 사이 친 편집을 다시 적는다 — 저장본과 같으면(dirty 아님) 보내지 않는다.
      autosavePending.current = true;
      sendAutosave();
    });
  }, [state.dirty, quote.id, draftScope, sendAutosave, setAutosaveFailed]);

  // 토스트의 「다시 시도」 가 그 시점의 save(최신 검증 상태)를 부르게 한다.
  const saveRef = useRef<(force?: boolean) => Promise<boolean>>(async () => false);
  const save = useCallback(async (force = false): Promise<boolean> => {
    if (savingRef.current) return false;
    // 잠긴 뒤 「다시 시도」 등으로 들어온 저장 — 조용히 끝내지 않고 까닭을 알린다.
    if (lockedRef.current !== null) {
      toast.error(lockedNoteOf(lockedRef.current));
      return false;
    }
    // 방금 시트가 버린 입력이 있으면 저장 토스트가 같은 자리(id)를 쓰고 그 사실을 설명에 싣는다.
    const abandoned = abandonedRef.current;
    abandonedRef.current = null;
    const merged =
      abandoned && Date.now() - abandoned.at < 1_500
        ? { id: SHEET_COMMIT_TOAST, note: `입력 취소: ${abandoned.text}` }
        : null;
    const withNote = (description?: string, action?: { label: string; onClick: () => void }) =>
      slotToast(merged?.id, [merged?.note, description].filter(Boolean).join("\n") || undefined, action);

    // 화면 계산이 막힌 상태의 값은 서버도 거부한다. 헛걸음 대신 그 자리에서 알린다.
    if (blocked) {
      toast.error(calcError ?? pctError ?? "입력을 확인해 주세요", withNote());
      return false;
    }
    // 화면은 안 막지만 **저장은 막는** 것 — 빈 관리비율이 여기 걸린다.
    if (pctSaveError !== null) {
      toast.error(pctSaveError, withNote());
      return false;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const sent = stateRef.current;
      const sentRev = revRef.current;
      const result = await saveQuoteDraft(
        quote.id,
        toDraftInput(sent),
        sentRev,
        force
      );
      if (!result.ok) {
        if (result.locked) {
          // 덮어쓰기로도 못 푸는 상태다 — 덮어쓰기 배너 대신 잠김 배너를 띄운다.
          markLocked(result.lock_reason ?? "sent", result.lock_head, true);
          toast.error(result.message, withNote());
          return false;
        }
        if (result.stale) {
          // 입력이 틀린 게 아니라 남이 먼저 저장한 것이다 — 고치라고 하지 않고
          // 덮어쓸지 묻는다. 배너도 같이 켠다 — 서버 문구는 이 토스트만 읽는다(live 영역은 비운다).
          setStaleSave({ rev: sentRev, message: result.message });
          setStale(true);
          toast.error(result.message, withNote());
          return false;
        }
        if (result.vat_locked) {
          // 그 사이 발송 등으로 작성 중이 아니게 됐다 — 방식만 되돌리면 나머지 편집은 저장된다.
          toast.error(result.message, withNote(VAT_REVERT_NOTE));
          return false;
        }
        toast.error(result.message, withNote(result.issues.slice(0, 3).join("\n")));
        return false;
      }
      dispatch({ type: "SAVED", amounts: result.amounts, sentKey: contentKeyOf(sent) });
      copySession.saved(sent);
      // 방금 내가 올린 번호다. 갱신하지 않으면 다음 자동저장이 자기 저장을
      // 「남이 건드렸다」 로 읽는다.
      syncRev(result.rev);
      postSaved(savedChannelRef.current, { quote_id: quote.id, rev: result.rev });
      autosaveGen.current += 1;
      inflightRef.current = null;
      setStale(false);
      setSuperseded(false);
      setConfirmOverwrite(false);
      setAutosavedAt(null);
      setAutosaveFailed(false);
      setOffer(null);
      ownSavedAtRef.current = null;
      clearMyDraft(draftScope);
      void discardQuoteDraft(quote.id).catch(() => undefined);
      toast.success(
        `저장했습니다 — ${formatWon(result.amounts.total)}`,
        withNote(droppedMessage(result.dropped_trades))
      );
      return true;
    } catch {
      toast.error(
        "저장하지 못했습니다",
        withNote(undefined, { label: "다시 시도", onClick: () => void saveRef.current(force) })
      );
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [quote.id, draftScope, blocked, calcError, pctError, pctSaveError, setOffer, markLocked, setSuperseded, setAutosaveFailed, copySession, syncRev, setStaleSave]);

  saveRef.current = save;

  /**
   * 「그래도 덮어쓰기」. 남이 저장한 것을 지운다는 뜻이라 한 번 더 묻는다.
   *
   * `window.confirm` 을 쓰지 않는다 — 네이티브 대화상자는 화면 밖이라 눈으로도
   * 자동 검증으로도 확인할 수 없고, 브라우저가 조용히 취소해 버리면 「눌렀는데
   * 아무 일도 안 일어난다」 가 된다. 배너 안에서 한 번 더 묻는다.
   */
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const overwrite = useCallback(() => {
    setConfirmOverwrite(false);
    void save(true);
  }, [save]);

  // 잠긴 견적의 화면 편집을 시트 붙여넣기용 TSV 로 복사 — writeText 는 클릭 안에서 바로 불러야 사용자 제스처로 인정된다.
  const copyEdits = useCallback(
    () =>
      copySession.copy(
        { state: stateRef.current, spaces, reason: lockedRef.current ?? "sent" },
        clipboardCopyDeps()
      ),
    [spaces, copySession]
  );

  /** 앱 안에서 떠날 때는 beforeunload 가 안 뜬다 — 여기서 한 번 물어본다. */
  const leave = useCallback(() => {
    if (
      state.dirty &&
      !window.confirm("저장하지 않은 편집이 있습니다. 그대로 나갈까요?")
    ) {
      return;
    }
    router.push(detailHref);
  }, [state.dirty, router, detailHref]);

  const quickAddBar =
    state.trades.length > 0 ? (
      <QuickAdd
        disabled={locked}
        isLocked={isLocked}
        simple={simple}
        spaces={spaces}
        suggestions={quickHits}
        trades={state.trades}
        onAdd={quickAdd}
        onPaste={quickPaste}
        onSearch={quickSearch}
      />
    ) : null;

  // 잠김·충돌 띠의 「새로 불러오기」 — 서버의 지금 내용으로 다시 연다(고친 게 있으면 이탈 경고가 먼저 묻는다).
  const reload = () => window.location.reload();

  // 늘 떠 있는 live 영역의 글 — 잠김·충돌 띠가 조건부로 붙어도 화면 읽기가 첫 알림을 놓치지 않는다.
  const conflictNote = conflictNoteOf({
    locked,
    lockNote: lockNote === undefined || lockToasted ? undefined : localOnlyNoteOf(lockNote),
    stale,
    superseded,
    staleSave,
    savedElsewhere,
  });

  return (
    <>
      {phone !== false && (
        <MobileEditorHead
          canRedo={history.future.length > 0}
          canUndo={history.past.length > 0}
          dispatch={dispatchUnlessLocked}
          locked={locked}
          moreRef={moreButtonRef}
          quoteNo={quote.quote_no}
          retry={state.dirty && autosaveFailed ? retryAutosave : null}
          setShowCost={setShowCost}
          setSimple={setSimple}
          showCost={showCost}
          simple={simple}
          status={phoneAutosaveLabelOf({ dirty: state.dirty, autosavedAt, failed: autosaveFailed, superseded, locked })}
          version={quote.version}
          onBack={leave}
          onImport={openImport}
          onQuickAdd={quickAddBar === null ? null : () => setQuickOpen(true)}
          onSaveTemplate={canSaveTemplate ? () => setTemplateOpen(true) : null}
        />
      )}
      {phone !== false && canSaveTemplate && (
        <SaveTemplateDialog
          dirty={state.dirty}
          locked={locked}
          open={templateOpen}
          quoteId={quote.id}
          returnFocus={returnMoreFocus}
          title={quote.title}
          onOpenChange={setTemplateOpen}
        />
      )}

      {phone !== true && (
      <div className="hidden md:contents">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-y-1">
          <div className="flex items-baseline gap-x-2">
            <h1 className="text-2xl font-semibold tracking-tight">견적 편집</h1>
            <span className="text-sm text-muted-foreground">
              {quote.quote_no} v{quote.version}
              {autosaveLabelOf({ dirty: state.dirty, autosavedAt, failed: autosaveFailed, superseded, locked })}
            </span>
            {state.dirty && autosaveFailed && (
              <Button
                size="inline"
                type="button"
                variant="link-destructive"
                onClick={retryAutosave}>
                저장 실패 — 다시 시도
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-x-2">
          <Button size="sm" variant="ghost" onClick={leave}>
            상세로
          </Button>
          {/* 비활성 단추는 포인터를 안 받아 title 이 안 뜨므로 감싼 span 에 단다. */}
          <span className="inline-flex" title={lockNote}>
            <Button
              disabled={saving || blocked || locked}
              size="sm"
              variant="primary"
              onClick={() => void save()}>
              저장
            </Button>
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="quote-editor-toolbar">
        <div className="flex items-center gap-x-2">
          <span className="text-xs text-muted-foreground" id="quote-editor-view-label">
            보기
          </span>
          <div
            aria-labelledby="quote-editor-view-label"
            className="flex rounded-md bg-muted p-0.5"
            role="group">
            <Button
              aria-pressed={view === "sheet"}
              size="sm"
              variant={view === "sheet" ? "secondary" : "ghost"}
              onClick={() => {
                // 단추로 넘어온 때만 표로 포커스를 돌려준다.
                if (view !== "sheet") sheetMemory.current = { ...(sheetMemory.current ?? { cursor: SHEET_START, anchor: SHEET_START }), focus: true };
                setView("sheet");
              }}>
              시트
            </Button>
            <Button
              aria-pressed={view === "card"}
              size="sm"
              variant={view === "card" ? "secondary" : "ghost"}
              onClick={() => setView("card")}>
              카드
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-x-2">
          <span className="text-xs text-muted-foreground" id="quote-editor-price-label">
            단가 입력
          </span>
          <div
            aria-labelledby="quote-editor-price-label"
            className="flex rounded-md bg-muted p-0.5"
            role="group">
            <Button
              aria-pressed={simple}
              size="sm"
              variant={simple ? "secondary" : "ghost"}
              onClick={() => setSimple(true)}>
              간단
            </Button>
            <Button
              aria-pressed={!simple}
              size="sm"
              variant={simple ? "ghost" : "secondary"}
              onClick={() => setSimple(false)}>
              상세
            </Button>
          </div>
          <span className="hidden text-xs text-muted-foreground lg:inline">
            {simple
              ? "자재·노무를 합친 단가 하나만 받습니다"
              : "자재와 노무를 나눠 받습니다"}
          </span>
        </div>
        <label className="flex min-h-10 items-center gap-x-2 text-xs text-muted-foreground" htmlFor="quote-editor-show-cost">
          <Switch
            aria-label="원가·마진 보기"
            checked={showCost}
            id="quote-editor-show-cost"
            onCheckedChange={setShowCost}
          />
          원가·마진 보기
        </label>
        {canSaveTemplate && (
          <div className="ml-auto">
            <SaveTemplateDialog dirty={state.dirty} locked={locked} quoteId={quote.id} title={quote.title} />
          </div>
        )}
      </div>
      </div>
      )}

      {offer && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
          <span className="text-sm">
            저장하지 않고 닫은 편집이 있습니다 — {formatDateTime(offer.draft.saved_at)}
            {offer.draft.base_rev !== baseRev && (
              <strong className="ml-1 font-medium text-destructive">
                (그 뒤 누가 저장했습니다 — 이어쓰면 그 내용을 덮습니다)
              </strong>
            )}
          </span>
          <div className="flex items-center gap-x-2">
            <Button disabled={discarding || locked} size="sm" variant="outline" onClick={restoreUnlessLocked}>
              이어쓰기
            </Button>
            {/* 잠겨도 둔다 — 내 초안 슬롯·이 탭 기록만 지우고 견적 본문·번호는 안 바꾼다(뒤따르는 자동저장은 잠김에 막힌다). */}
            <Button disabled={discarding} size="sm" variant="ghost" onClick={() => void discard()}>
              버리기
            </Button>
          </div>
        </div>
      )}

      {lockNote !== undefined && <LockedBanner note={lockNote} onCopy={() => void copyEdits()} onLeave={leave} onReload={reload} />}

      <p className="sr-only" data-testid="quote-editor-conflict-live" role="status">
        {conflictNote}
      </p>

      {stale && !locked && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span>{staleNoteOf(staleSave, superseded)}</span>
          <Button size="sm" variant="outline" onClick={reload}>
            새로 불러오기
          </Button>
          {confirmOverwrite ? (
            <span className="flex items-center gap-x-2">
              <span className="text-sm">그 사람의 저장을 지웁니다.</span>
              <Button
                disabled={saving}
                size="sm"
                variant="destructive"
                onClick={overwrite}>
                덮어쓰기
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmOverwrite(false)}>
                취소
              </Button>
            </span>
          ) : (
            <Button
              disabled={saving}
              size="sm"
              variant="destructive"
              onClick={() => setConfirmOverwrite(true)}>
              그래도 덮어쓰기
            </Button>
          )}
        </div>
      )}

      {savedElsewhere && !stale && !locked && (
        // 읽어 주기는 늘 떠 있는 conflict live 영역 하나만 — 이 띠는 live={false} 로 role 없는 보이는 글.
        <div className="flex flex-wrap items-center gap-2">
          <Banner className="min-w-0 flex-1" live={false} testId="quote-editor-saved-elsewhere" tone="warning">
            {SAVED_ELSEWHERE_NOTE}
          </Banner>
          <Button size="sm" variant="outline" onClick={reload}>
            새로 불러오기
          </Button>
        </div>
      )}

      {others.length > 0 && !stale && !locked && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {others.join(" · ")} 님도 지금 이 견적을 편집 중입니다.
        </div>
      )}

      {blocked && (
        <div
          className="flex items-start gap-x-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-y-0.5">
            <span className="font-medium">
              {calcError ?? pctError} — 저장할 수 없습니다
            </span>
            <span className="text-xs">
              아래 금액은 고치기 전 마지막 계산값입니다. 값을 바로잡으면 저장이
              다시 열립니다.
            </span>
          </div>
        </div>
      )}

      {phone !== false && (
        <MobileEditor
          autoRounding={defaultRounding(liveAmounts.gross)}
          canAddMissing={canAddMissing}
          cost={liveResult}
          customerName={customerName}
          customerSite={customerSite}
          dispatch={dispatch}
          fillNote={canFillTemplate(state, templateByTrade) ? fillTemplateNoteOf(state.area_pyeong) : null}
          ignoredCount={allMissing.length - missing.length}
          live={state.dirty || !state.saved}
          lockNote={lockNote}
          isLocked={isLocked}
          missing={missing}
          missingTrades={missingTrades}
          needsArea={needsArea}
          pctError={pctError}
          phases={phases}
          calibration={calibration}
          standard={standard}
          saveDisabled={saving || blocked || locked}
          setSimple={setSimple}
          showCost={showCost}
          shown={shown}
          simple={simple}
          spaces={spaces}
          state={state}
          status={quote.status}
          templateByTrade={templateByTrade}
          tradePhases={tradePhases}
          validDays={validDays}
          onAddMissing={addMissing}
          onIgnoreMissing={(id) => updateIgnored([...ignored.filter((v) => v !== id), id])}
          onResetIgnored={() => updateIgnored([])}
          onSave={() => void save()}
          onSuggest={onSuggest}
          quickAdd={quickAddBar}
          quickOpen={quickOpen}
          onQuickOpenChange={setQuickOpen}
          quickReturnFocus={returnMoreFocus}
        />
      )}

      {phone !== true && (
      <div className="hidden md:contents">
      {/* 칸을 떠나면 입력 묶음을 끊는다 — 공종명·조건·평수·카드 입력이 다음 입력과 한 번에 되돌려지지 않게. */}
      <div
        className={cn(
          "grid gap-4",
          view === "card" && "lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px]"
        )}
        ref={editorRootRef}
        onBlurCapture={() => dispatch({ type: "SEAL" })}>
        <div className="flex min-w-0 flex-col gap-y-4">
          {/* 잠기면 본문을 읽기 전용으로 — fieldset 셋이 입력·단추를 끄고(시트는 숨은 캐럿이 살아 있게 밖에 둔다), 포인터다운 Radix 트리거는 disabled 를 따로 받는다. */}
          <fieldset aria-describedby={lockedDescribedBy(locked)} className="flex min-w-0 flex-col gap-y-4" data-testid="quote-editor-body" disabled={locked}>
          <SectionCard className="grid gap-3 md:grid-cols-2" padding="sm">
            <Field label="공사명">
              <Input
                disabled={locked}
                inputSize="sm"
                name="title"
                value={state.title}
                onChange={(e) =>
                  dispatchUnlessLocked({ type: "SET_TITLE", value: e.target.value })
                }
              />
            </Field>

            <div className="flex flex-col gap-y-1">
              <span className="text-xs text-muted-foreground">고객</span>
              <p className="min-h-9 px-2 py-1 text-sm">
                {customerName}
                {customerSite && (
                  <span className="text-muted-foreground"> · {customerSite}</span>
                )}
              </p>
            </div>

            <div className="flex flex-col gap-y-1">
              <span className="text-xs text-muted-foreground">유효기간</span>
              <p className="min-h-9 px-2 py-1 text-sm">
                발송일로부터 {validDays}일
              </p>
            </div>

            <AreaInput
              disabled={locked}
              invalid={needsArea}
              value={stateAreaM2(state)}
              onChange={(value) => dispatchUnlessLocked({ type: "SET_AREA_M2", value })}
            />

            <Field label="관리비율 (%)">
              <div className="w-24">
                <Input
                  aria-invalid={pctError !== null}
                  className="text-right tabular-nums"
                  disabled={locked}
                  inputMode="decimal"
                  inputSize="sm"
                  name="mgmt_fee_pct"
                  value={state.mgmt_fee_pct}
                  onChange={(e) =>
                    dispatchUnlessLocked({ type: "SET_MGMT_PCT", value: e.target.value })
                  }
                />
              </div>
              {pctError && (
                <span className="text-xs text-destructive">{pctError}</span>
              )}
            </Field>

            <VatModeField
              priceCount={vatSwitchPriceCount(state)}
              disabled={locked}
              isLocked={isLocked}
              status={quote.status}
              value={vatMode}
              onChange={(value) => dispatch({ type: "SET_VAT_MODE", value, status: quote.status })}
            />
          </SectionCard>
          </fieldset>

          {/* fieldset 밖 — 칩 펼치기·무시·무시 해제는 보기 설정이라 잠겨도 되고, 「추가」 만 lockReason 으로 끈다. */}
          <MissingPanel
            canAdd={canAddMissing}
            ignoredCount={allMissing.length - missing.length}
            lockReason={lockReason}
            suggestions={missing}
            onAdd={addMissing}
            onIgnore={(id) => updateIgnored([...ignored.filter((v) => v !== id), id])}
            onResetIgnored={() => updateIgnored([])}
          />

          {/* 빈 fieldset 이 gap 을 한 번 더 먹지 않게 담을 게 있을 때만 그린다. */}
          {(canFillTemplate(state, templateByTrade) || quickAddBar !== null || view === "sheet") && (
          <fieldset aria-describedby={lockedDescribedBy(locked)} className="flex min-w-0 flex-col gap-y-4" data-testid="quote-editor-body-tail" disabled={locked}>
          {/* 공종 뼈대뿐일 때만 — 시트·카드 공통 자리, 한 번에 넣고 되돌리기 한 번으로 전부 뺀다. */}
          {canFillTemplate(state, templateByTrade) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2">
              <span className="text-sm text-muted-foreground">{fillTemplateNoteOf(state.area_pyeong)}</span>
              <Button
                disabled={locked}
                size="sm"
                variant="outline"
                onClick={() => dispatchUnlessLocked({ type: "FILL_TEMPLATE", byTrade: templateByTrade })}>
                <Plus className="size-4" /> 기본 템플릿 항목 한 번에 넣기
              </Button>
            </div>
          )}

          {quickAddBar}

          {view === "sheet" && (
            <SectionCard as="div" className="flex flex-wrap items-baseline justify-end gap-x-4 gap-y-1 px-3 py-2 text-sm" padding="none">
              <span className="text-xs text-muted-foreground">
                {state.dirty || !state.saved ? "저장 전 계산값" : "저장된 값"}
              </span>
              <span className="text-muted-foreground">
                총계 <Money className="font-medium text-foreground" value={shown.gross} />
              </span>
              <span className="text-muted-foreground">
                최종 견적금액{" "}
                <Money className="text-base font-bold text-foreground" value={shown.total} />
              </span>
            </SectionCard>
          )}

          </fieldset>
          )}

          {view === "sheet" && (
            <SheetView
              disabled={locked}
              isLocked={isLocked}
              canRedo={history.future.length > 0}
              canUndo={history.past.length > 0}
              dispatch={dispatch}
              memory={sheetMemory}
              showCost={showCost}
              simple={simple}
              spaces={spaces}
              vatMode={vatMode}
              state={state}
              templateByTrade={templateByTrade}
              importButtonRef={importButtonRef}
              jumpTo={sheetJump}
              onJumped={onJumped}
              onAbandon={onAbandon}
              onImport={openImport}
              onSuggest={onSuggest}
            />
          )}

          <fieldset aria-describedby={lockedDescribedBy(locked)} className="flex min-w-0 flex-col gap-y-4" data-testid="quote-editor-body-rest" disabled={locked}>
          {view === "card" && TRADE_GROUPS.map((group) => {
            const trades = state.trades.filter((t) => t.group === group);
            const subtotal =
              group === "BASIC" ? shown.subtotal_basic : shown.subtotal_style;

            return (
              <div key={group} className="flex flex-col gap-y-2">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-lg font-semibold">
                    {TRADE_GROUP_LABEL[group]}
                  </h2>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    소계 <Money value={subtotal} />
                  </span>
                </div>

                {trades.length === 0 && (
                  <EmptyState>
                    공종이 없습니다.
                  </EmptyState>
                )}

                {trades.map((trade) => (
                  <TradeCard
                    key={trade.key}
                    disabled={locked}
                    isLocked={isLocked}
                    area={state.area_pyeong}
                    areaM2={stateAreaM2(state)}
                    dispatch={dispatch}
                    showCost={showCost}
                    simple={simple}
                    spaces={spaces}
                    templateItems={templateByTrade.get(trade.trade) ?? []}
                    vatMode={vatMode}
                    trade={trade}
                    onSuggest={onSuggest}
                  />
                ))}
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu open={openUnlessLocked(tradeMenuOpen, locked)} onOpenChange={setTradeMenuOpen}>
              {/* 비활성은 Trigger 에 단다 — 자식 Button 에만 달면 Radix 가 포인터다운으로 빈 메뉴를 연다. */}
              <DropdownMenuTrigger asChild disabled={missingTrades.length === 0 || locked}>
                <Button size="sm" variant="outline">
                  <Plus className="size-4" /> 공종 추가
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
                {missingTrades.map((def) => (
                  <DropdownMenuItem
                    key={def.code}
                    onSelect={() =>
                      dispatchUnlessLocked({
                        type: "ADD_TRADE",
                        trade: def.code,
                        name: def.name,
                        group: def.group,
                        sort_order: def.sort_order,
                      })
                    }>
                    {def.name}
                    <span className="text-xs text-muted-foreground">
                      {TRADE_GROUP_LABEL[def.group]}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {view === "card" && (
              <Button ref={importButtonRef} disabled={locked} size="sm" variant="outline" onClick={openImport}>
                <FileSpreadsheet className="size-4" /> 엑셀 가져오기
              </Button>
            )}
          </div>

          <SchedulePanel
            areaM2={stateAreaM2(state)}
            calibration={calibration}
            disabled={locked}
            dispatch={dispatch}
            isLocked={isLocked}
            lockNotedAbove
            phases={phases}
            schedule={state.schedule}
            standard={standard}
            tradePhases={tradePhases}
            trades={state.trades}
          />

          <CoverEditor
            conditionKeys={state.condition_keys}
            conditions={state.conditions}
            disabled={locked}
            dispatch={dispatch}
            extraNotice={state.extra_notice}
            isLocked={isLocked}
          />
          </fieldset>
        </div>

        <div
          className={cn(
            view === "card"
              ? "lg:sticky lg:top-16 lg:self-start"
              : "ml-auto w-full max-w-sm"
          )}>
          <fieldset aria-describedby={lockedDescribedBy(locked)} className="min-w-0" data-testid="quote-editor-totals" disabled={locked}>
          <TotalsPanel
            amounts={shown}
            autoRounding={defaultRounding(liveAmounts.gross)}
            disabled={locked}
            dispatch={dispatch}
            cost={liveResult}
            isLocked={isLocked}
            live={state.dirty || !state.saved}
            rounding={state.rounding}
            showCost={showCost}
            vatMode={vatMode}
          />
          </fieldset>
        </div>
      </div>
      </div>
      )}

      {/* 닫으면 연 단추로 — 폰은 머리 더보기, 데스크톱은 「엑셀 가져오기」 단추(폰에는 없다). */}
      <ImportDialog
        areaPyeong={state.area_pyeong}
        open={openUnlessLocked(importOpen, locked)}
        orgId={quote.org_id}
        spaces={spaces}
        tradeOptions={tradeOptions}
        trades={state.trades}
        vatMode={vatMode}
        returnFocus={phone === true ? returnMoreFocus : returnImportFocus}
        onImport={onImport}
        onOpenChange={setImportOpen}
      />
    </>
  );
};

export default QuoteEditor;
